/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

/* Import i18n functions */
import { useI18n } from '@/context/I18nContext';

/* Import types */
import type { DebugRequestEntry } from '@/types/ui';

/* Import libraries */
import { formatImportedAt } from '@/lib/formatting/dateTime';

interface DebugTabProps {
    entries: DebugRequestEntry[]
    toDebugString: (value: unknown) => string
}

function DebugTab({ entries, toDebugString }: DebugTabProps) {
    const { locale, t } = useI18n();
    const sortedEntries = [...entries].reverse();

    return (
        <div>
            <div className="grid gap-3">
                {sortedEntries.map((entry) => (
                    <div key={entry.id} className="card border border-base-300 bg-base-200 shadow-sm debug-entry">
                        <div className="card-body p-5">
                            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                                <div className="font-semibold">{t('Request #{id}', { id: entry.id })}</div>
                                <div className="flex flex-wrap items-center gap-2">
                                    <span className="text-sm text-base-content/60">
                                        {formatImportedAt(entry.requestedAt, locale, t('Unknown'))}
                                    </span>
                                    <span className="text-sm text-base-content/60">
                                        {entry.durationMs !== null ? `${entry.durationMs} ms` : t('n/a')}
                                    </span>
                                    <span
                                        className={`badge ${
                                            entry.success ? 'badge-success' : 'badge-error'
                                        }`}
                                    >
                                        {entry.success ? t('OK') : t('Error')}
                                    </span>
                                </div>
                            </div>

                            <div className="grid gap-3 xl:grid-cols-2">
                                <div>
                                    <div className="mb-1 text-sm text-base-content/60">{t('URL')}</div>
                                    <pre className="debug-pre status-code">{entry.url}</pre>
                                </div>

                                {entry.originalRequest ? (
                                    <div>
                                        <div className="mb-1 text-sm text-base-content/60">
                                            {t('Original request')}
                                        </div>
                                        <pre className="debug-pre status-code">
                                            {toDebugString(entry.originalRequest)}
                                        </pre>
                                    </div>
                                ) : null}

                                <div>
                                    <div className="mb-1 text-sm text-base-content/60">
                                        {t('Raw response preview')}
                                    </div>
                                    <pre className="debug-pre status-code">
                                        {entry.responsePreview}
                                    </pre>
                                </div>

                                <div>
                                    <div className="mb-1 text-sm text-base-content/60">
                                        {t('Parameters')}
                                    </div>
                                    <pre className="debug-pre status-code">
                                        {toDebugString(entry.parameters)}
                                    </pre>
                                </div>

                                <div>
                                    <div className="mb-1 text-sm text-base-content/60">
                                        {t('Other parameters')}
                                    </div>
                                    <pre className="debug-pre status-code">
                                        {toDebugString(entry.otherParameters)}
                                    </pre>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default DebugTab;
